export const CASE_STUDY_LOCALES = ['en', 'sk'];
export const DEFAULT_CASE_STUDY_LOCALE = 'en';
export const CASE_STUDY_LOCALE_OVERRIDE_KEY = 'caseStudyLocaleOverride';

export const caseStudyLanguageLabels = {
  en: 'EN',
  sk: 'SK',
};

export const caseStudyLocaleNames = {
  en: 'English',
  sk: 'Slovak',
};

export const caseStudyLanguageFlags = {
  en: {
    src: '/assets/flags/gb.svg',
  },
  sk: {
    src: '/assets/flags/sk.svg',
  },
};

export const caseStudyUi = {
  en: {
    indexTitle: 'Case Studies',
    indexDescription: 'Selected client work, implementation decisions, and measurable outcomes.',
    eyebrow: 'Client outcomes',
    emptyTitle: 'Case studies are being prepared.',
    emptyDescription: '',
    contactCta: 'Start a project',
    blogCta: 'Read the blog',
    backToStudies: 'Back to case studies',
    published: 'Published',
    updated: 'Updated',
    client: 'Client',
    website: 'Website',
    industry: 'Industry',
    location: 'Location',
    language: 'Language',
    similarProject: 'Build a similar project',
    challengeStats: 'Before',
    timeline: 'Timeline',
    technologies: 'Technologies',
    additionalResults: 'Additional results',
    notFoundTitle: 'Case study not found',
    notFoundDescription: 'This case study is not published yet or does not have this language version.',
    viewCaseStudy: 'View case study',
  },
  sk: {
    indexTitle: 'Prípadové štúdie',
    indexDescription: 'Vybrané klientské projekty, implementačné rozhodnutia a merateľné výsledky.',
    eyebrow: 'Výsledky klientov',
    emptyTitle: 'Prípadové štúdie pripravujeme.',
    emptyDescription: '',
    contactCta: 'Spustiť projekt',
    blogCta: 'Prečítať blog',
    backToStudies: 'Späť na prípadové štúdie',
    published: 'Publikované',
    updated: 'Aktualizované',
    client: 'Klient',
    website: 'Web',
    industry: 'Odvetvie',
    location: 'Lokalita',
    language: 'Jazyk',
    similarProject: 'Chcem podobný projekt',
    challengeStats: 'Predtým',
    timeline: 'Časový rámec',
    technologies: 'Technológie',
    additionalResults: 'Ďalšie výsledky',
    notFoundTitle: 'Prípadová štúdia sa nenašla',
    notFoundDescription: 'Táto prípadová štúdia ešte nie je publikovaná alebo nemá túto jazykovú verziu.',
    viewCaseStudy: 'Zobraziť štúdiu',
  },
};

export const caseStudies = [
  {
    slug: 'napen',
    clientName: 'NAPEN',
    industry: 'Events',
    location: 'Slovakia',
    url: 'https://napen.sk',
    image: '/assets/case-studies/napen/napen.webp',
    imageAlt: 'Modern NAPEN website presentation with bold visual design, service overview, and contact paths for an event agency',
    publishedDate: '2026-04-20',
    lastModified: '2026-04-20',
    public: true,
    featured: true,
    translations: {
      en: {
        metaTitle: 'Website and Internal System for NAPEN',
        metaDescription: 'An event agency went from zero online demand to 10+ corporate calls a month, while internal tools save 12 h / week. Read the story.',
        industry: 'Events',
        location: 'Slovakia',
        imageAlt: 'Modern NAPEN website presentation with bold visual design, service overview, and contact paths for an event agency',
        tagline: 'From zero to 10+ calls a month: a website and internal tools for an event agency.',
        summary: 'NAPEN had no online presence and managed events through spreadsheets, email threads, and phone calls. We built a distinctive landing page for corporate clients plus internal tools for managing events, suppliers, venues, budgets, and client approvals.',
        cardDescription: 'An event agency with no online presence needed a credible way to win new corporate clients and control internal delivery. The new website now generates 10+ calls a month, while the internal system saves 12 h / week.',
        challenge: {
          title: 'Before',
          description: 'NAPEN is an event agency in Slovakia that organizes corporate events ranging from conferences and celebrations to festivals and exhibitions. The team had delivered dozens of successful projects, but it had never had its own website. All business came through personal relationships, referrals, and phone calls. When a corporate client searched online for an event supplier, NAPEN effectively did not exist. Competing agencies with professional presentations were winning work that NAPEN had the expertise and capacity to deliver. Internally, the situation looked similar: event planning happened in Excel, supplier and venue communication was split across dozens of email threads, budgets lived in spreadsheets, and tasks stayed in peoples heads. Every new event brought hours of organizational chaos.',
          problems: [
            'No website, so the agency was invisible online to new clients',
            'Complete dependence on referrals and personal relationships',
            'No way to show the service offer and event formats online',
            'Event planning in Excel, with budgets and timelines outside a central system',
            'Supplier and venue communication fragmented across dozens of email threads',
            'Potential partners had no clear way to discover NAPEN and start cooperation',
          ],
          stats: [
            { label: 'Online inquiries', value: '0 / month', icon: 'chart-down' },
            { label: 'Online presence', value: 'None', icon: 'x' },
            { label: 'Project management', value: 'Excel + email', icon: 'x' },
          ],
        },
        solution: {
          title: 'What We Built',
          description: 'The solution had two parts. First, we designed and developed a visually distinctive landing page focused on a strong first impression and direct contact. Visitors can immediately understand the service offer and call or write without friction. The page also includes a partner section for building B2B cooperation. Second, we built internal tools for managing the full event pipeline, from the first client contact through planning, supplier communication, and final billing.',
          features: [
            'A visually distinctive one-page website with premium design, smooth animations, and subtle transitions',
            'Service sections with cards for individual event types and an offer for external partners',
            'Partner section with a CTA for new B2B cooperation',
            'Contact section with direct phone numbers and email for immediate inquiries',
            'Internal event management system with deal pipeline, task assignment, deadlines, and statuses',
            'Supplier and venue database with ratings, capacities, and pricing',
            'Budget module tracking costs against budget in real time for every event',
            'Client portal where corporate customers can see preparation status, approve items, and communicate with the team',
          ],
          timeline: '9 weeks from design to launch',
          technologies: ['React', 'Tailwind CSS', 'Framer Motion', 'shadcn/ui', 'Symfony', 'PostgreSQL'],
        },
        results: {
          title: 'Results',
          description: 'The website generated the first call from a new client in the launch week. Within three months, it stabilized at 10+ inbound calls a month from corporate customers who found NAPEN through Google or a shared website link. The bold animated design builds trust and sets NAPEN apart from competing agencies. The internal system reduced the average time spent organizing events by 12 h / week because the team now has everything in one place instead of searching through emails and spreadsheets. The client portal reduced check-in calls from corporate customers by -65% because clients can verify preparation progress themselves.',
          quote: 'Before, I spent hours every day searching through emails and spreadsheets. Now I open the system and see the status of every event, every supplier, every budget. And companies call us from the website that would never have found us before. It is a completely different level.',
          quoteAuthor: 'Dávid, Managing Director at NAPEN',
          rating: 5,
          metrics: [
            { label: 'Website calls per month', value: '10+', description: 'from corporate clients who found NAPEN online', icon: 'trending-up', color: 'amber' },
            { label: 'Time saved', value: '12 h / week', description: 'through centralized event and supplier management', icon: 'zap', color: 'sage' },
            { label: 'Client check-in calls', value: '-65%', description: 'because clients verify progress themselves in the portal', icon: 'chart-line', color: 'sage' },
            { label: 'New partnerships', value: '4 companies', description: 'new partners came through the website partner section', icon: 'users', color: 'amber' },
          ],
          additionalResults: [
            'The distinctive animated website builds trust and sets NAPEN apart from competing agencies',
            'Direct phone contacts on the website generate qualified leads because callers already have a concrete event idea',
            'The internal supplier and venue database eliminated repeated searching and negotiation',
            'The budget module reduced budget overruns to under 5%',
            'The NAPEN team hired two new people thanks to growth in new projects',
            'The client portal improved satisfaction among corporate customers',
          ],
        },
      },
      sk: {
        metaTitle: 'Web a interný systém pre NAPEN',
        metaDescription: 'Eventová agentúra prešla z nulového online dopytu na 10+ firemných telefonátov mesačne. Interný systém navyše šetrí 12 h / týždeň. Celý príbeh.',
        industry: 'Eventy',
        location: 'Slovensko',
        imageAlt: 'Moderná webová prezentácia NAPEN s výrazným vizuálnym dizajnom, prehľadom služieb a kontaktmi pre eventovú agentúru',
        tagline: 'Z nuly na 10+ telefonátov mesačne. Web a interné nástroje pre eventovú agentúru.',
        summary: 'NAPEN nemal žiadnu online prezentáciu a eventy riadil cez tabuľky, e-mailové vlákna a telefonáty. Vytvorili sme výraznú landing page pre firemných klientov a interné nástroje na riadenie eventov, dodávateľov, priestorov, rozpočtov a klientskych schválení.',
        cardDescription: 'Eventová agentúra bez online prezentácie potrebovala dôveryhodný spôsob, ako získavať nových firemných klientov a mať pod kontrolou internú realizáciu. Nový web dnes generuje 10+ telefonátov mesačne a interný systém šetrí 12 h / týždeň.',
        challenge: {
          title: 'Situácia predtým',
          description: 'NAPEN je eventová agentúra na Slovensku, ktorá organizuje firemné akcie od konferencií a osláv až po festivaly a výstavy. Tím mal za sebou desiatky úspešných realizácií, no nikdy nemal vlastný web. Celý biznis fungoval cez osobné kontakty, odporúčania a telefonáty. Keď firemný klient hľadal dodávateľa eventu na internete, NAPEN tam prakticky neexistoval. Konkurenčné agentúry s profesionálnou prezentáciou získavali zákazky, na ktoré mal NAPEN kvalifikáciu aj kapacitu. Podobne to vyzeralo aj interne: plánovanie eventov prebiehalo v Exceli, komunikácia s dodávateľmi a prenajímateľmi priestorov bola roztrúsená v desiatkach e-mailových vlákien, rozpočty boli v tabuľkách a úlohy v hlavách ľudí. Každá nová akcia znamenala hodiny organizačného chaosu.',
          problems: [
            'Žiadny web, takže agentúra bola online neviditeľná pre nových klientov',
            'Úplná závislosť od odporúčaní a osobných kontaktov',
            'Nemožnosť ukázať ponuku služieb a typy eventov online',
            'Plánovanie eventov v Exceli, rozpočty a harmonogramy bez centrálneho systému',
            'Komunikácia s dodávateľmi a venues roztrúsená v desiatkach e-mailových vlákien',
            'Potenciálni partneri nemali jasný spôsob, ako NAPEN nájsť a nadviazať spoluprácu',
          ],
          stats: [
            { label: 'Dopyty z internetu', value: '0 / mesiac', icon: 'chart-down' },
            { label: 'Online prezentácia', value: 'Žiadna', icon: 'x' },
            { label: 'Riadenie projektov', value: 'Excel + e-mail', icon: 'x' },
          ],
        },
        solution: {
          title: 'Čo sme vytvorili',
          description: 'Riešenie malo dve časti. Najprv sme navrhli a vyvinuli vizuálne výraznú landing page so zameraním na silný prvý dojem a priamy kontakt. Návštevník okamžite vidí ponuku služieb a môže rovno zavolať alebo napísať. Súčasťou je aj partnerská sekcia na nadväzovanie B2B spolupráce. Následne sme vytvorili interné nástroje na riadenie celého eventového pipeline, od prvého kontaktu s klientom cez plánovanie a komunikáciu s dodávateľmi až po vyúčtovanie.',
          features: [
            'Vizuálne výrazný jednostránkový web s prémiovým dizajnom, plynulými animáciami a jemnými prechodmi',
            'Sekcia služieb s kartami jednotlivých typov eventov a ponukou pre externých partnerov',
            'Partnerská sekcia s CTA na nadväzovanie B2B spolupráce',
            'Kontaktná sekcia s priamymi telefónnymi číslami a e-mailom pre okamžitý dopyt',
            'Interný event management systém: pipeline zákaziek, priradenie úloh, termíny a statusy',
            'Databáza dodávateľov a venues s hodnotením, kapacitami a cenami',
            'Rozpočtový modul: sledovanie nákladov oproti rozpočtu v reálnom čase pre každú akciu',
            'Klientsky portál, kde firemný zákazník vidí stav príprav, schvaľuje položky a komunikuje s tímom',
          ],
          timeline: '9 týždňov od návrhu po spustenie',
          technologies: ['React', 'Tailwind CSS', 'Framer Motion', 'shadcn/ui', 'Symfony', 'PostgreSQL'],
        },
        results: {
          title: 'Výsledky',
          description: 'Web priniesol prvý telefonát od nového klienta ešte v týždni spustenia. Počas troch mesiacov sa ustálil na 10+ prichádzajúcich telefonátoch mesačne od firemných zákazníkov, ktorí NAPEN našli cez Google alebo cez zdieľaný odkaz na web. Výrazný dizajn s animáciami buduje dôveru a odlišuje NAPEN od konkurencie. Interný systém skrátil čas strávený organizáciou eventov v priemere o 12 h / týždeň, pretože tím má všetko na jednom mieste namiesto hľadania v e-mailoch a tabuľkách. Klientsky portál znížil počet kontrolných telefonátov od firemných zákazníkov o -65%, keďže si priebeh príprav vedia overiť sami.',
          quote: 'Predtým som strávil hodiny denne tým, že som hľadal v mailoch a tabuľkách. Teraz otvorím systém a vidím stav každej akcie, každého dodávateľa, každý rozpočet. A z webu nám volajú firmy, ktoré by nás predtým nikdy nenašli. To je úplne iný level.',
          quoteAuthor: 'Dávid, konateľ NAPEN',
          rating: 5,
          metrics: [
            { label: 'Telefonáty z webu mesačne', value: '10+', description: 'od firemných klientov, ktorí našli NAPEN online', icon: 'trending-up', color: 'amber' },
            { label: 'Úspora času', value: '12 h / týždeň', description: 'vďaka centrálnemu riadeniu eventov a dodávateľov', icon: 'zap', color: 'sage' },
            { label: 'Kontrolné telefonáty klientov', value: '-65%', description: 'klienti si stav príprav overia sami v portáli', icon: 'chart-line', color: 'sage' },
            { label: 'Nové partnerstvá', value: '4 firmy', description: 'noví partneri prišli cez partnerskú sekciu webu', icon: 'users', color: 'amber' },
          ],
          additionalResults: [
            'Vizuálne výrazný web s animáciami buduje dôveru a odlišuje NAPEN od konkurenčných agentúr',
            'Priame telefónne kontakty na webe generujú kvalitné leady, pretože klient volá s konkrétnou predstavou',
            'Interná databáza dodávateľov a venues odstránila opakované hľadanie a vyjednávanie',
            'Rozpočtový modul znížil prekročenie rozpočtu na menej ako 5%',
            'Tím NAPEN prijal dve nové posily vďaka nárastu zákaziek',
            'Klientsky portál zvýšil spokojnosť firemných zákazníkov',
          ],
        },
      },
    },
  },
  {
    slug: 'ngrhook',
    clientName: 'NGRHOOK',
    industry: 'SaaS / Software',
    url: 'https://ngrhook.club',
    image: '/assets/case-studies/ngrhook/ngrhook.webp',
    imageAlt: 'NGRHOOK SaaS platform dashboard with subscription management, partner program, and payment automation',
    beforeImage: '/assets/case-studies/ngrhook/ngrhook-before.webp',
    afterImage: '/assets/case-studies/ngrhook/ngrhook-after.webp',
    publishedDate: '2026-03-01',
    lastModified: '2026-03-01',
    public: true,
    featured: true,
    translations: {
      en: {
        metaTitle: 'NGRHOOK SaaS Platform',
        metaDescription: 'We rebuilt a 2007 forum into a SaaS platform generating €80,000+ per year. Stripe, React, Symfony, partner program, and automated payments.',
        industry: 'SaaS / Software',
        imageAlt: 'NGRHOOK SaaS platform dashboard with subscription management, partner program, and payment automation',
        tagline: 'From a legacy 2007 forum to a SaaS platform generating €80,000+ per year.',
        summary: 'NGRHOOK was running its software product on a 17-year-old forum system that was never designed for e-commerce, subscriptions, or partners. We rebuilt it into a modern SaaS platform with automated payments, license management, Discord integration, and a white-label partner program.',
        cardDescription: 'A full rebuild of a 17-year-old forum system into a modern SaaS platform with automated Stripe and crypto payments, a white-label partner program, Discord integration, and a zero-downtime migration of thousands of users.',
        beforeImageCaption: 'Original forum-based system before the SaaS rebuild.',
        afterImageCaption: 'Current NGRHOOK dashboard after the SaaS rebuild.',
        afterImageLabel: 'After',
        challenge: {
          title: 'Before',
          description: 'NGRHOOK ran its software product on a critically outdated forum engine from 2007, a system that had never been designed for e-commerce. Users registered through the forum, payments were handled manually through PayPal, and license management meant editing database records by hand. A partner program was not realistic because each new reseller created hours of manual work. The system was held together by inertia.',
          problems: [
            'Forum engine from 2007 with no support or security patches',
            'Manual PayPal payment handling with no automation',
            'No partner program, with resellers handled ad hoc',
            'No audit trail or reliable view of platform activity',
            'Security gaps including SQL injection and missing 2FA',
            'No practical way to scale because every new customer required manual work',
          ],
          stats: [
            { label: 'System age', value: '17 years', icon: 'calendar-old' },
            { label: 'Payment automation', value: '0%', icon: 'x' },
            { label: 'Security', value: 'Critical', icon: 'shield-off' },
          ],
        },
        solution: {
          title: 'What We Built',
          description: 'We designed and developed a complete SaaS platform from scratch, including a full migration of users, licenses, and payment history from the 17-year-old system. No customer lost access and no record was lost. The new platform covers the full customer lifecycle from registration and payment to subscription management and a white-label partner program.',
          features: [
            'Custom authentication with 2FA, Discord OAuth2, and HWID validation',
            'Multi-gateway payments through Stripe, crypto payments via NowPayments, and Sellix',
            'White-label partner program with full rebranding options',
            'Subscription management with freeze, unfreeze, and automatic expiration',
            'REST API for loader authorization, versioning, and partner integrations',
            'Admin panel, support panel, partner panel, and user dashboard',
            'Gamification with games and an Advent calendar for retention',
            'Discord integration with automatic roles, webhooks, and notifications',
            'Complete database migration: thousands of users, licenses, and payment records without downtime',
          ],
          timeline: '12 weeks from architecture design to production launch',
          technologies: ['Symfony', 'React', 'Material-UI', 'Stripe', 'NowPayments', 'Discord API'],
        },
        results: {
          title: 'Results',
          description: 'The platform now runs reliably and generates more than €80,000 per year. The partner program added a new sales channel without additional marketing spend, while payments, licenses, Discord roles, and notifications run almost entirely automatically.',
          quote: 'Before, I handled every payment and every license manually. Now it all runs by itself, partners sell under their own brand, and I have visibility.',
          quoteAuthor: 'Owner of NGRHOOK',
          rating: 5,
          metrics: [
            { label: 'Annual revenue', value: '€80,000+', description: 'stable recurring revenue', icon: 'trending-up', color: 'amber' },
            { label: 'Automation', value: '98%', description: 'payments, licenses, and notifications', icon: 'zap', color: 'sage' },
            { label: 'Partner program', value: 'White-label', description: 'resellers with their own brand', icon: 'users', color: 'amber' },
            { label: 'Partner revenue', value: '30%', description: 'new channel without marketing spend', icon: 'trending-up', color: 'sage' },
          ],
          additionalResults: [
            'Complete audit trail with a record of every important action',
            'Discord bot automatically assigns roles based on subscription status',
            'Partner program generates 30% of total revenue',
            'Average support ticket resolution dropped from hours to minutes',
            'Zero-downtime deployment with automatic migrations',
          ],
        },
      },
      sk: {
        metaTitle: 'SaaS platforma NGRHOOK',
        metaDescription: 'Zastarané fórum z roku 2007 sme prestavali na SaaS platformu s obratom 80 000+ € ročne. Stripe, React, Symfony, partnerský program a automatizované platby.',
        industry: 'SaaS / softvér',
        imageAlt: 'SaaS platforma NGRHOOK s dashboardom na správu predplatného, partnerským programom a automatizáciou platieb',
        tagline: 'Zo zastaraného fóra z roku 2007 na SaaS platformu s obratom 80 000+ € ročne.',
        summary: 'NGRHOOK prevádzkoval svoj softvérový produkt na 17-ročnom fórovom systéme, ktorý nikdy nebol navrhnutý pre e-commerce, predplatné ani partnerov. Prestavali sme ho na modernú SaaS platformu s automatizovanými platbami, správou licencií, Discord integráciou a white-label partnerským programom.',
        cardDescription: 'Kompletná prestavba 17-ročného fórového systému na modernú SaaS platformu s automatizovanými platbami cez Stripe a kryptomeny, white-label partnerským programom, Discord integráciou a migráciou tisícov používateľov bez výpadku.',
        beforeImageCaption: 'Pôvodný fórový systém pred prestavbou na SaaS platformu.',
        afterImageCaption: 'Aktuálny dashboard NGRHOOK po prestavbe na SaaS platformu.',
        afterImageLabel: 'Potom',
        challenge: {
          title: 'Situácia predtým',
          description: 'NGRHOOK prevádzkoval svoj softvérový produkt na kriticky zastaranom fórovom engine z roku 2007, systéme, ktorý nikdy nebol navrhnutý pre e-commerce. Používatelia sa registrovali cez fórum, platby prebiehali ručne cez PayPal a správa licencií znamenala ručnú úpravu databázových záznamov. O partnerskom programe nemohla byť reč, pretože každý nový reseller znamenal hodiny manuálnej práce. Systém držal pokope hlavne zotrvačnosťou.',
          problems: [
            'Fórový engine z roku 2007 bez podpory a bezpečnostných záplat',
            'Ručná správa platieb cez PayPal bez akejkoľvek automatizácie',
            'Žiadny partnerský program, reselleri riešení ad hoc',
            'Nulový audit trail bez spoľahlivého prehľadu o aktivitách',
            'Bezpečnostné diery vrátane SQL injection a chýbajúceho 2FA',
            'Nemožnosť škálovať, pretože každý nový zákazník znamenal manuálnu prácu',
          ],
          stats: [
            { label: 'Vek systému', value: '17 rokov', icon: 'calendar-old' },
            { label: 'Automatizácia platieb', value: '0%', icon: 'x' },
            { label: 'Bezpečnosť', value: 'Kritická', icon: 'shield-off' },
          ],
        },
        solution: {
          title: 'Čo sme vytvorili',
          description: 'Navrhli a vyvinuli sme kompletnú SaaS platformu od nuly vrátane kompletnej migrácie používateľov, licencií a platobnej histórie zo 17-ročného systému. Ani jeden zákazník neprišiel o prístup a ani jeden záznam sa nestratil. Nová platforma pokrýva celý životný cyklus zákazníka od registrácie cez platbu až po správu predplatného a white-label partnerský program.',
          features: [
            'Vlastná autentifikácia s 2FA, Discord OAuth2 a HWID validáciou',
            'Multi-gateway platby cez Stripe, kryptomeny cez NowPayments a Sellix',
            'White-label partnerský program s kompletnými možnosťami rebrandingu',
            'Správa predplatného s freeze, unfreeze a automatickým expirovaním',
            'REST API pre autorizáciu loadera, verzie a partnerské napojenia',
            'Admin panel, support panel, partner panel a používateľský dashboard',
            'Gamifikácia s hrami a adventným kalendárom pre retenciu',
            'Discord integrácia s automatickými rolami, webhookmi a notifikáciami',
            'Kompletná migrácia databázy: tisíce používateľov, licencie a platobné záznamy bez výpadku',
          ],
          timeline: '12 týždňov od návrhu architektúry po produkčné nasadenie',
          technologies: ['Symfony', 'React', 'Material-UI', 'Stripe', 'NowPayments', 'Discord API'],
        },
        results: {
          title: 'Výsledky',
          description: 'Platforma dnes beží stabilne a generuje viac ako 80 000 EUR ročne. Partnerský program priniesol nový predajný kanál bez dodatočných nákladov na marketing, zatiaľ čo platby, licencie, Discord roly a notifikácie fungujú takmer úplne automaticky.',
          quote: 'Predtým som každú platbu a každú licenciu riešil ručne. Teraz to celé beží samo, partneri predávajú pod vlastným brandom a ja mám prehľad.',
          quoteAuthor: 'Majiteľ NGRHOOK',
          rating: 5,
          metrics: [
            { label: 'Ročný obrat', value: '80 000+ €', description: 'stabilný recurring revenue', icon: 'trending-up', color: 'amber' },
            { label: 'Automatizácia', value: '98%', description: 'platby, licencie a notifikácie', icon: 'zap', color: 'sage' },
            { label: 'Partnerský program', value: 'White-label', description: 'reselleri s vlastným brandom', icon: 'users', color: 'amber' },
            { label: 'Tržby z partnerov', value: '30%', description: 'nový kanál bez nákladov na marketing', icon: 'trending-up', color: 'sage' },
          ],
          additionalResults: [
            'Kompletný audit trail so záznamom každej dôležitej akcie',
            'Discord bot automaticky priraďuje roly podľa stavu predplatného',
            'Partnerský program generuje 30% celkových tržieb',
            'Priemerný čas riešenia support ticketu klesol z hodín na minúty',
            'Zero-downtime deployment s automatickými migráciami',
          ],
        },
      },
    },
  },
  {
    slug: 'iq-apartments',
    clientName: 'IQ Apartments',
    industry: 'Accommodation',
    location: 'Prešov',
    url: 'https://iq-apartments.com',
    image: '/assets/case-studies/iq-apartments/iq-apartments.webp',
    imageAlt: 'IQ Apartments booking system with online payments and an apartment availability calendar',
    publishedDate: '2026-03-05',
    lastModified: '2026-03-05',
    public: true,
    featured: true,
    translations: {
      en: {
        metaTitle: 'Booking System for IQ Apartments',
        metaDescription: 'A €2,900 website generated €3,500 in direct bookings in the first month. From zero to 55% commission-free bookings. The IQ Apartments case study.',
        industry: 'Accommodation',
        location: 'Prešov',
        imageAlt: 'IQ Apartments booking system with online payments and an apartment availability calendar',
        tagline: 'A €2,900 website generated €3,500 in direct bookings in the first month.',
        summary: 'IQ Apartments ran premium apartments in Prešov, but every reservation came through Booking.com and Airbnb. We built a direct booking platform with online payments, real-time availability, and a multilingual interface so guests could book without commission-heavy intermediaries.',
        cardDescription: 'Premium apartments in Prešov were dependent on Booking.com and Airbnb with 15-18% commissions. The new booking system paid back in less than a month and today 55% of reservations arrive directly through the website.',
        challenge: {
          title: 'Before',
          description: 'IQ Apartments operated premium apartments in Prešov, but all reservations came exclusively through Booking.com and Airbnb. The owners paid high commissions and had no control over their own clients.',
          quote: 'Every reservation cost us 15-18% in commission. We could not build our own client database and we were dependent on the algorithms of booking platforms.',
          quoteAuthor: 'Denys Mazur, owner of IQ Apartments',
          problems: [
            '15-18% commission on every reservation',
            'No ability to build an owned brand',
            'Dependency on third-party platforms',
            'No direct contact with guests',
          ],
          stats: [
            { label: 'Platform commissions', value: '€1,200', icon: 'money-lost' },
            { label: 'Direct bookings', value: '0%', icon: 'chart-down' },
            { label: 'Customer control', value: 'None', icon: 'x' },
          ],
        },
        solution: {
          title: 'What We Built',
          description: 'We created a complete web platform with a proprietary booking system that competes with booking platforms. Guests can browse apartments, check real-time availability, reserve, and pay online.',
          features: [
            'Proprietary booking system with real-time availability',
            'Integrated payment gateway for cards, Google Pay, and Apple Pay',
            'Multilingual interface in SK, EN, and UK',
            'Automatic confirmation emails',
            'Central interface for managing the whole operation',
            'Connection with Booking.com and Airbnb',
          ],
          timeline: '6 weeks from design to launch',
          technologies: ['Symfony', 'React', 'Material-UI', 'Stripe', 'Google Maps API', 'Booking.com API', 'Airbnb API'],
        },
        results: {
          title: 'Results',
          description: 'Direct commission-free bookings reached €3,500 in the first month of operation.',
          quote: 'The website cost us €2,900 and €3,500 went through direct bookings in the very first month. The payback was immediate. And every following month it keeps growing.',
          quoteAuthor: 'Denys Mazur, owner of IQ Apartments',
          rating: 5,
          metrics: [
            { label: 'Direct bookings in month one', value: '€3,500', description: 'from direct commission-free reservations', icon: 'trending-up', color: 'amber' },
            { label: 'Website cost', value: '€2,900', description: 'for the complete booking system', icon: 'piggy-bank', color: 'sage' },
            { label: 'Payback period', value: '< 1 month', description: 'the investment returned immediately', icon: 'chart-line', color: 'sage' },
            { label: 'Commission savings', value: '15-18%', description: 'on every direct reservation', icon: 'users', color: 'amber' },
          ],
          additionalResults: [
            '55% of all reservations now come directly through the website',
            'Owned client database for remarketing',
            'Ability to offer exclusive discounts for direct bookings',
            'Full control over apartment pricing and availability',
          ],
        },
      },
      sk: {
        metaTitle: 'Rezervačný systém pre IQ Apartments',
        metaDescription: 'Web za €2,900 priniesol €3,500 v priamych rezerváciách za prvý mesiac. Z nuly na 55% rezervácií bez provízií. Prípadová štúdia IQ Apartments.',
        industry: 'Ubytovanie',
        location: 'Prešov',
        imageAlt: 'Rezervačný systém IQ Apartments s online platbami a kalendárom dostupnosti apartmánov',
        tagline: 'Web za €2,900 priniesol €3,500 v priamych rezerváciách už prvý mesiac.',
        summary: 'IQ Apartments prevádzkovali prémiové apartmány v Prešove, no všetky rezervácie prichádzali cez Booking.com a Airbnb. Vytvorili sme vlastnú rezervačnú platformu s online platbami, dostupnosťou v reálnom čase a viacjazyčným rozhraním, aby hostia mohli rezervovať bez províznych sprostredkovateľov.',
        cardDescription: 'Prémiové apartmány v Prešove boli závislé od Booking.com a Airbnb s províziami 15-18%. Nový rezervačný systém sa zaplatil za menej ako mesiac a dnes 55% rezervácií prichádza priamo cez web.',
        challenge: {
          title: 'Situácia predtým',
          description: 'IQ Apartments prevádzkovali prémiové apartmány v Prešove, ale všetky rezervácie prichádzali výhradne cez Booking.com a Airbnb. Majitelia platili vysoké provízie a nemali kontrolu nad vlastnými klientmi.',
          quote: 'Každá rezervácia nás stála 15-18% provízie. Nemohli sme budovať vlastnú databázu klientov a boli sme závislí od algoritmov booking platforiem.',
          quoteAuthor: 'Denys Mazur, majiteľ IQ Apartments',
          problems: [
            '15-18% provízia za každú rezerváciu',
            'Nemožnosť budovať vlastnú značku',
            'Závislosť od platforiem tretích strán',
            'Žiadny priamy kontakt s hosťami',
          ],
          stats: [
            { label: 'Provízie platformám', value: '€1,200', icon: 'money-lost' },
            { label: 'Priame rezervácie', value: '0%', icon: 'chart-down' },
            { label: 'Kontrola nad zákazníkmi', value: 'Žiadna', icon: 'x' },
          ],
        },
        solution: {
          title: 'Čo sme vytvorili',
          description: 'Vytvorili sme kompletnú webovú platformu s vlastným rezervačným systémom, ktorý dokáže konkurovať booking platformám. Hostia si môžu prezerať apartmány, kontrolovať dostupnosť v reálnom čase, rezervovať a zaplatiť online.',
          features: [
            'Vlastný rezervačný systém s dostupnosťou v reálnom čase',
            'Integrovaná platobná brána pre karty, Google Pay a Apple Pay',
            'Viacjazyčné rozhranie v jazykoch SK, EN a UK',
            'Automatické potvrdzovacie e-maily',
            'Centrálne rozhranie na správu celej prevádzky',
            'Prepojenie s Booking.com a Airbnb',
          ],
          timeline: '6 týždňov od návrhu po spustenie',
          technologies: ['Symfony', 'React', 'Material-UI', 'Stripe', 'Google Maps API', 'Booking.com API', 'Airbnb API'],
        },
        results: {
          title: 'Výsledky',
          description: 'Priame rezervácie bez provízií dosiahli €3,500 hneď v prvom mesiaci prevádzky.',
          quote: 'Web nás stál €2,900 a cez priame rezervácie prešlo €3,500 hneď prvý mesiac. Návratnosť bola okamžitá. A každý ďalší mesiac to rastie.',
          quoteAuthor: 'Denys Mazur, majiteľ IQ Apartments',
          rating: 5,
          metrics: [
            { label: 'Priame rezervácie v prvom mesiaci', value: '€3,500', description: 'z priamych rezervácií bez provízií', icon: 'trending-up', color: 'amber' },
            { label: 'Cena webu', value: '€2,900', description: 'za kompletný rezervačný systém', icon: 'piggy-bank', color: 'sage' },
            { label: 'Návratnosť', value: '< 1 mesiac', description: 'investícia sa vrátila okamžite', icon: 'chart-line', color: 'sage' },
            { label: 'Úspora na províziách', value: '15-18%', description: 'z každej priamej rezervácie', icon: 'users', color: 'amber' },
          ],
          additionalResults: [
            '55% všetkých rezervácií dnes prichádza priamo cez web',
            'Vlastná databáza klientov pre remarketing',
            'Možnosť ponúkať exkluzívne zľavy pre priame rezervácie',
            'Plná kontrola nad cenami a dostupnosťou apartmánov',
          ],
        },
      },
    },
  },
];

export const isSupportedCaseStudyLocale = (locale) => CASE_STUDY_LOCALES.includes(locale);

export const normalizeCaseStudyLocale = (locale) => (
  isSupportedCaseStudyLocale(locale) ? locale : DEFAULT_CASE_STUDY_LOCALE
);

const getCaseStudyStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const getCaseStudyLocaleOverride = () => {
  const storage = getCaseStudyStorage();
  let storedLocale = null;

  try {
    storedLocale = storage?.getItem(CASE_STUDY_LOCALE_OVERRIDE_KEY);
  } catch {
    storedLocale = null;
  }

  return isSupportedCaseStudyLocale(storedLocale) ? storedLocale : null;
};

export const setCaseStudyLocaleOverride = (locale) => {
  if (!isSupportedCaseStudyLocale(locale)) {
    return;
  }

  const storage = getCaseStudyStorage();

  try {
    storage?.setItem(CASE_STUDY_LOCALE_OVERRIDE_KEY, locale);
  } catch {
    // Ignore storage failures; navigation still applies the selected locale for this visit.
  }
};

export const getBrowserCaseStudyLocale = () => {
  if (typeof navigator === 'undefined') {
    return DEFAULT_CASE_STUDY_LOCALE;
  }

  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  const browserLocale = browserLanguages
    .map((language) => language?.toLowerCase().split('-')[0])
    .find((language) => isSupportedCaseStudyLocale(language));

  return browserLocale || DEFAULT_CASE_STUDY_LOCALE;
};

export const getPreferredCaseStudyLocale = () => (
  getCaseStudyLocaleOverride() || getBrowserCaseStudyLocale()
);

export const getCaseStudyPath = (locale, slug = '') => {
  const normalizedLocale = normalizeCaseStudyLocale(locale);
  return slug ? `/${normalizedLocale}/case-studies/${slug}` : `/${normalizedLocale}/case-studies`;
};

export const hasCaseStudyTranslation = (study, locale) => (
  Boolean(study?.translations?.[locale])
);

export const getLocalizedCaseStudy = (slug, locale) => {
  const study = caseStudies.find((item) => item.slug === slug && item.public !== false);

  if (!study || !hasCaseStudyTranslation(study, locale)) {
    return null;
  }

  return {
    ...study,
    locale,
    content: study.translations[locale],
  };
};

export const getLocalizedCaseStudies = (locale) => (
  caseStudies
    .filter((study) => study.public !== false && hasCaseStudyTranslation(study, locale))
    .map((study) => ({
      ...study,
      locale,
      content: study.translations[locale],
    }))
    .sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime())
);

export const getAlternateCaseStudyPath = (study, locale) => {
  if (!study || !hasCaseStudyTranslation(study, locale)) {
    return null;
  }

  return getCaseStudyPath(locale, study.slug);
};
